import React, { useState, useEffect } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,
  Paper,
  IconButton,
  Grid,
  CircularProgress,
  Tooltip,
  Button,
  Switch,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import { visuallyHidden } from "@mui/utils";
import "@fontsource/roboto/500.css";
import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "ID",
    numeric: true,
    label: "ID",
  },
  {
    id: "name",
    numeric: false,
    label: "Group",
  },
  {
    id: "active",
    numeric: false,
    label: "Active",
  },
  {
    id: "edit",
    numeric: false,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            style={{ padding: "4px" }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  const { numSelected, setShowGroupForm } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <>
          <Typography
            sx={{ flex: "1 1 100%" }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            Groups
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setShowGroupForm(true);
            }}
          >
            add group
          </Button>
        </>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

function Groups({ setShowGroupForm }) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { store } = useStateContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [groupId, setGroupId] = useState();

  const handleClose = () => {
    // Close the dialog
    setDialogOpen(false);
  };

  const handleChange = (e) => {
    // Update the new value state
    setNewValue(e.target.value);
  };

  const handleSubmit = () => {
    // Close the dialog
    const correctStoreName = JSON.parse(store).name.replace(/[^a-zA-Z]/g,"");
    const databaseStoreName = `DB301_${JSON.parse(store).id}_${correctStoreName}`;
    
    axiosClient.patch(`groups/group/${groupId}`, {
      params: {
        name: newValue,
        shopName: databaseStoreName,
      },
    }).then(()=> window.location.reload())
    .catch((err) => console.log(err))

    setDialogOpen(false);
  };

  useEffect(() => {
    setLoading(true);
    const correctStoreName = JSON.parse(store).name.replace(/[^a-zA-Z]/g,"");
    const databaseStoreName = `DB301_${JSON.parse(store).id}_${correctStoreName}`;

    axiosClient
      .get(`groups/${databaseStoreName}`)
      .then((response) => {
        setRows(response.data.data);
        setLoading(false);
      })
      .catch((err) => console.log(err));
  }, [store]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  if (loading) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar setShowGroupForm={setShowGroupForm} />
        <TableContainer>
          <Table
            sx={{ minWidth: 400 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.id}
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell>
                      <Switch
                        defaultChecked={row.display}
                        onClick={() =>{
                          const correctStoreName = JSON.parse(store).name.replace(/[^a-zA-Z]/g,"");
                          const databaseStoreName = `DB301_${JSON.parse(store).id}_${correctStoreName}`;

                          axiosClient.delete(`groups/group/${row.id}`, {
                            params: {
                              display: !row.display,
                              shopName: databaseStoreName,
                            },
                          })
                        }
                        }
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      onClick={() => {
                        setDialogOpen(true)
                        setNewValue(row.name)
                        setGroupId(row.id)
                      }}
                    >
                      <EditIcon />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 33 * emptyRows,
                  }}
                >
                  <TableCell colSpan={13} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Dialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle>{`Change group name`}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Value"
            type="text"
            value={newValue}
            onChange={handleChange}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Groups;
